import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, filter } from 'rxjs';
import { ROLES } from 'src/app/features/authentication/authRoles.constants';
import {
  AssetManagementState,
  FetchMrtSpecificationsAction,
  ResetAssetManagamentStateAction
} from 'src/app/shared/state/AssetManagementState';
import { FetchAllProductsAction } from 'src/app/shared/state/ProductsState';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { environment } from 'src/environments/environment';
import { hasRolePermission } from '../../../features/user-management/utils/UserSetupUtils';
import { FEATURE_FLAGS } from '../../../shared/constants/constants';
import { FeatureflagsService } from '../../../shared/services/featureflags.service';
import { TranslationService } from '../../../shared/translations/services/translation.service';
import { DealerState, UpdatePosEnabledAction } from '../../../shared/state/DealerState';
import { UserManagementService } from '../../../features/user-management/services/user-management.service';
import { BreadcrumbContextService } from '../../../shared/services/breadcrumb-context.service';
import { DateFormatService } from '../../../shared/services/date-format.service';
import { DealerDataService } from '../../../features/setup-screens/services/dealer-data.service';
import { AuthenticationService } from '../../../features/authentication/services/authentication.service';
import { LaoWindowService } from '../../../shared/services/lao-window.service';
import jwtDecode from 'jwt-decode';
import { JwtToken } from '../../../shared/models/jwt-token.model';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  standalone: false
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  @ViewChildren('sidebarItem') sidebarItems: QueryList<any>;
  @ViewChild('mainContentArea', { read: ElementRef }) mainContentArea: ElementRef;
  @ViewChild('scrollAnchor', { read: ElementRef }) scrollAnchor: ElementRef;

  // Replace constructor-injected services with inject() assignments
  public store = inject(Store);
  public cdr = inject(ChangeDetectorRef);
  public featureflagsService = inject(FeatureflagsService);
  public breadcrumbContextService = inject(BreadcrumbContextService);
  public router = inject(Router);
  public translationService = inject(TranslationService);
  public userManagementService = inject(UserManagementService);
  public dateFormatService = inject(DateFormatService);
  public dealerDataService = inject(DealerDataService);
  private readonly authService = inject(AuthenticationService);
  private readonly laoWindowService = inject(LaoWindowService);

  isTranslationsLoading$: Observable<boolean>;
  enabled: boolean;
  isUserMgmtEnabled = false;
  isWorkOrderEnabled = false;
  isAuditLogsScreenEnabled = false;
  isUserManualEnabled = false;
  isPosEnabled = false;
  isDatasetDownloadEnabled = false;
  isLegacyButtonVisible = false;
  isLaoMenuVisible = false;
  dealerUrl: string;

  editMode = false;

  constructor() {
    this.store.dispatch(new FetchAllProductsAction());
    this.store.dispatch(new FetchMrtSpecificationsAction());
    // Initialize date format service with default or api value
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (currentDealer?.id) {
      this.dealerDataService.getDealerDateFormat(currentDealer.id).subscribe({
        next: (response: any) => {
          const dateFormat = response.data;
          if (dateFormat) {
            this.dateFormatService.setFormat(dateFormat.toUpperCase());
          }
        },
        error: (err) => {
          console.error('Failed to fetch dealer date format:', err);
          this.dateFormatService.setFormat('DD/MM/YYYY'); // Fallback to default
        }
      });
    } else {
      this.dateFormatService.setFormat('DD/MM/YYYY'); // Fallback to default when no dealer
    }
  }

  ngOnInit() {
    this.isUserMgmtEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.USER_MGMT_ENABLED);
    this.isWorkOrderEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.WORK_ORDER_ENABLED);
    this.isAuditLogsScreenEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.AUDIT_LOGS_SCREEN_ENABLED);
    this.isDatasetDownloadEnabled = this.featureflagsService.getFeatureFlagValue('isDatasetDownloadEnabled');
    this.isLegacyButtonVisible = this.featureflagsService.getFeatureFlagValue('isLegacyButtonVisible');
    this.isLaoMenuVisible = this.featureflagsService.getFeatureFlagValue('isLaoMenuVisible');
    this.isUserManualEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.USER_MANUAL_ENABLED);
    this.isTranslationsLoading$ = this.store.select(TranslationState.loading);
    const dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.dealerUrl = dealer?.dealerUrl ? new URL(dealer.dealerUrl).toString() : '';
    this.initPosEnabled();

    this.store.select(AssetManagementState.editing).subscribe((editMode) => {
      this.editMode = editMode;
    });

    // Prod-customer-enable
    this.enabled = this.canViewCustomerSettings();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel)).subscribe((event) => {
      if (event instanceof NavigationCancel) {
        this.updateActiveSidebarItem();
      } else if (event instanceof NavigationEnd) {
        this.handlePotentialContextClearingNavigation(event.url);
        this.updateActiveSidebarItem();
        this.breadcrumbContextService.setProgrammaticNavigation(false);
        setTimeout(() => this.scrollContentToTop(), 50);
      }
    });
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ResetAssetManagamentStateAction());
  }

  canViewCustomerSettings(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(ROLES.view_customer);
  }

  canViewWorkOrders(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(ROLES.view_work_orders) || roles.includes(ROLES.create_work_orders) || roles.includes(ROLES.edit_work_orders);
  }

  canEditDealerSetup(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(ROLES.edit_dealer_setup);
  }

  canViewPosInterface(): boolean {
    const roles = this.authService.getRolesFromToken();
    return this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.POS_INTERFACE_ENABLED) && roles.includes(ROLES.view_pos_interface);
  }

  /**
   * Initialize POS enabled state from DealerState or fetch from API once.
   * The value is stored in DealerState and updated when Dealer Setup saves.
   */
  private initPosEnabled(): void {
    // Subscribe to DealerState for reactive updates
    this.store.select(DealerState.posEnabled).subscribe((posEnabled) => {
      this.isPosEnabled = posEnabled;
      this.cdr.markForCheck();
    });

    // Fetch from API only if no value exists in state
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (currentDealer && currentDealer.posEnabled === undefined) {
      this.dealerDataService.getPosEnabled(currentDealer.id).subscribe({
        next: (response: any) => {
          const posEnabled = response.data === true || response.data === 'Y';
          this.store.dispatch(new UpdatePosEnabledAction(posEnabled));
        },
        error: (err) => {
          console.error('Failed to fetch pos setting:', err);
          this.store.dispatch(new UpdatePosEnabledAction(false));
        }
      });
    }
  }

  updateActiveSidebarItem() {
    const currentUrl = this.router.url;

    // Check if there's a preferred sidebar link from navigation context
    const preferredLink = this.breadcrumbContextService.getPreferredActiveSidebarLink();

    let activeSidebarItem;
    if (preferredLink) {
      // Use the preferred link from navigation context (e.g., when coming from casing search)
      activeSidebarItem = this.sidebarItems.find((item) => item.link === preferredLink);
    } else {
      // Use default URL-based matching
      activeSidebarItem = this.sidebarItems.find((item) => currentUrl.includes(item.link));
    }

    this.sidebarItems.forEach((item) => {
      item.active = activeSidebarItem ? activeSidebarItem.link : '';
    });
  }

  /**
   * Handle explicit sidebar item clicks
   * This clears navigation context when user intentionally switches modules
   */
  onWOSidebarItemClick(link: string): void {
    // Handle context clearing for explicit navigation
    this.breadcrumbContextService.handleSidebarNavigation(link);

    // Navigate to the clicked link
    this.router.navigate([link]);
  }

  onSidebarItemClick(event: MouseEvent, link?: string) {
    // Special handling for Product Data: navigate to first tab (Adjustment Reasons) when already on product data
    if (link === '/home/productData' && this.router.url.startsWith('/home/productData')) {
      event.preventDefault();
      event.stopPropagation();
      // Navigate to the first alphabetical tab (Adjustment Reasons)
      this.router.navigate(['/home/productData/adjustmentReasons']);
      const target = event.target as HTMLElement;
      if (target && typeof target.blur === 'function') {
        target.blur();
      }
      setTimeout(() => {
        this.refreshSidebarScroll();
      }, 200);
      return;
    }
    if (link === '/home/setup' && this.router.url.startsWith('/home/setup')) {
      event.preventDefault();
      event.stopPropagation();
      this.router.navigate(['/home/setup']);
      const target = event.target as HTMLElement;
      if (target && typeof target.blur === 'function') {
        target.blur();
      }
      setTimeout(() => {
        this.refreshSidebarScroll();
      }, 200);
      return;
    }
    const target = event.target as HTMLElement;
    if (target && typeof target.blur === 'function') {
      target.blur();
    }
    setTimeout(() => {
      this.refreshSidebarScroll();
    }, 200);
  }

  /**
   * Programmatically open or close the sidebar by clicking the real toggler button.
   * The toggler button is in layout.component.html and manages its own state internally.
   * We read the current open/closed state from the button's aria-expanded attribute
   * and only click if we need to change the state.
   */
  private setSidebarOpen(open: boolean): void {
    const toggler = document.getElementById('white-sidebar');
    if (!toggler) return;
    const button = toggler.querySelector('button');
    if (!button) return;
    const isCurrentlyOpen = button.getAttribute('aria-expanded') === 'true';
    if (open !== isCurrentlyOpen) {
      button.click();
    }
  }

  /**
   * Handle potential context clearing when user navigates via sidebar (not programmatic)
   * This catches navigation through the sidebar items that don't have explicit click handlers
   */
  private handlePotentialContextClearingNavigation(currentUrl: string): void {
    // Only clear context if this is NOT programmatic navigation
    if (!this.breadcrumbContextService.isProgrammaticNavigationActive()) {
      this.breadcrumbContextService.handleSidebarNavigation(currentUrl);
    }
  }

  get canViewCasingGrade(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(ROLES.view_casing_grade) || roles.includes(ROLES.edit_casing_grade);
  }

  get canViewWheelAndTireServices(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(ROLES.view_services) || roles.includes(ROLES.edit_services);
  }

  get canViewEmailSetup(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(ROLES.manage_emails);
  }

  protected readonly environment = environment;
  protected readonly ROLES = ROLES;
  protected readonly hasRolePermission = hasRolePermission;

  async openLegacyBtn() {
    const dealerId = this.store.selectSnapshot(DealerState.currentDealer).id;

    this.userManagementService.legacyToken(dealerId).subscribe({
      next: (response: any) => {
        const token = response.headers.get('Authorization')?.replace('Bearer ', ''); // Fetch token from header
        if (token) {
          const url = `${this.dealerUrl}gra/sso-login?token=${token}`;
          window.open(url);
        } else {
          console.error('Token is not available in the response header');
        }
      },
      error: (err) => {
        console.error('Failed to fetch legacy token:', err);
      }
    });
  }

  openLaoApp(): void {
    // The token is delivered via a postMessage handshake (never placed in the URL, so it
    // can't leak into browser history, the address bar, server logs or Referer headers).
    const payload = {
      type: 'btn-sso-handoff',
      token: localStorage.getItem('token') ?? '',
      dealerId: localStorage.getItem('dealerId') ?? '',
      userId: localStorage.getItem('userId') ?? '',
      firstName: localStorage.getItem('firstName') ?? '',
      lastName: localStorage.getItem('lastName') ?? '',
      customer: localStorage.getItem('customer') ?? ''
    };
    const laoOrigin = new URL(this.environment.labourOverheadServiceUrl).origin;

    // Open with a marker only; the LAO tab requests the token once its listener is ready.
    const laoWindow = window.open(`${this.environment.labourOverheadServiceUrl}lao/home/dashboard#sso=1`, '_blank');
    this.laoWindowService.set(laoWindow);

    let readyTimeout: ReturnType<typeof setTimeout>;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== laoOrigin || event.data?.type !== 'btn-sso-ready') {
        return;
      }
      (event.source as Window | null)?.postMessage(payload, laoOrigin);
      window.removeEventListener('message', onMessage);
      clearTimeout(readyTimeout);
    };
    window.addEventListener('message', onMessage);
    // Safety cleanup if the LAO tab never signals ready (e.g. popup blocked or closed).
    readyTimeout = setTimeout(() => window.removeEventListener('message', onMessage), 10000);
  }

  private scrollContentToTop(): void {
    // scrollIntoView lets the browser find the correct scroll container automatically,
    // including across shadow DOM boundaries used by mic-main-content / mic-sidebar-container.
    if (this.scrollAnchor?.nativeElement) {
      this.scrollAnchor.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private refreshSidebarScroll() {
    const sidebarContainer = document.querySelector('mic-sidebar-container');
    if (sidebarContainer) {
      const event = new Event('resize');
      window.dispatchEvent(event);

      (sidebarContainer as HTMLElement).style.overflow = 'auto';
      // Force reflow to ensure sidebar height recalculates (explicit side-effect)
      void (sidebarContainer as HTMLElement).offsetHeight;
      const needsScroll = (sidebarContainer as HTMLElement).scrollHeight > (sidebarContainer as HTMLElement).clientHeight;

      setTimeout(() => {
        if (needsScroll) {
          (sidebarContainer as HTMLElement).style.overflow = 'auto';
        } else {
          (sidebarContainer as HTMLElement).style.overflow = 'hidden';
        }
      }, 10);
    }
  }

  openUserManual(): void {
    const userManualPath = 'assets/user-manual/USER_MANUAL_INDEX.html';
    window.open(userManualPath, '_blank');
  }

  checkTokenForAuthority(tokenName: string): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const decoded = jwtDecode<JwtToken>(token.replace('Bearer ', ''));
      return decoded.roles.includes(tokenName);
    } catch {
      return false;
    }
  }
}
